import type { EnemyDef } from '@playnets/sim';

/**
 * `value` is the juice a kill drops, and it is deliberately tiny: fodder is
 * worth 1, elites 3–4, a boss 25. A wave should be countable on your fingers.
 *
 * Enemies are silhouettes only — flat `threat` fills, no interior detail (§10).
 * `colour` is the family tint used while they hold on the spawn ring; silhouette
 * plus colour is the entire read.
 *
 * `stageMin`/`stageMax` are the game's main difficulty dial (§6.1). Shorten them
 * and the game becomes a reflex test; lengthen them and every wave is solved
 * before it starts. Tune here, never in the spawner.
 */

// Region grounds are saturated mid-darks now, not near-black, so the threat
// family had to come up with them. An enemy that needs a second look is a
// failure of the silhouette rule (§10), not a mood.
const THREAT = 0xff4f6b;
const THREAT_DARK = 0xe0325a;
const THREAT_PALE = 0xff97ab;

export const ENEMIES: readonly EnemyDef[] = [
  {
    id: 'drone',
    name: 'Drone',
    behaviour: 'fan',
    hp: 14,
    speed: 62,
    size: 12,
    value: 1,
    damage: 3,
    stageMin: 3.0,
    stageMax: 3.8,
    colour: THREAT_PALE,
  },
  {
    id: 'asteroid',
    name: 'Asteroid',
    behaviour: 'splitter',
    hp: 90,
    speed: 26,
    size: 26,
    value: 2,
    damage: 10,
    stageMin: 4.2,
    stageMax: 5.0,
    colour: THREAT,
    splitInto: 'shard',
    splitCount: 2,
  },
  {
    id: 'shard',
    name: 'Shard',
    behaviour: 'straight',
    hp: 26,
    speed: 46,
    size: 14,
    value: 1,
    damage: 4,
    stageMin: 0,
    stageMax: 0,
    colour: THREAT_PALE,
  },
  {
    id: 'cruiser',
    name: 'Cruiser',
    behaviour: 'shielded',
    hp: 150,
    speed: 32,
    size: 24,
    value: 3,
    damage: 12,
    stageMin: 4.0,
    stageMax: 4.8,
    colour: THREAT_DARK,
    // The shield faces the planet, so only a flanking arc gets through.
    shieldArc: 0.95,
    shieldMult: 0.2,
  },
  {
    id: 'burrower',
    name: 'Burrower',
    behaviour: 'burrow',
    hp: 55,
    speed: 54,
    size: 16,
    value: 2,
    damage: 8,
    stageMin: 3.4,
    stageMax: 4.2,
    colour: 0xd45c9e,
  },
  {
    id: 'carrier',
    name: 'Carrier',
    behaviour: 'carrier',
    hp: 240,
    speed: 24,
    size: 30,
    value: 4,
    damage: 14,
    stageMin: 4.5,
    stageMax: 5.2,
    colour: THREAT_DARK,
    parkR: 258,
    spawnEvery: 2.6,
    spawns: 'drone',
  },
  {
    id: 'wraith',
    name: 'Wraith',
    behaviour: 'cloak',
    hp: 70,
    speed: 50,
    size: 18,
    value: 2,
    damage: 10,
    stageMin: 3.6,
    stageMax: 4.4,
    colour: 0xb08cf0,
    cloakRadius: 120,
  },
  {
    id: 'feint',
    name: 'Feint',
    behaviour: 'feint',
    hp: 80,
    speed: 58,
    size: 18,
    value: 3,
    damage: 12,
    stageMin: 3.2,
    stageMax: 4.0,
    colour: 0xffa04d,
    // Commits to bearing A the whole staging flight, then veers this far off it.
    veerAt: 246,
    veerSpread: 1.15,
  },
  {
    id: 'world-eater',
    name: 'World Eater',
    behaviour: 'boss',
    hp: 2600,
    speed: 16,
    size: 62,
    value: 25,
    damage: 45,
    stageMin: 6,
    stageMax: 6,
    colour: 0xff2d55,
    phases: 3,
  },

  /* --- Regional natives -------------------------------------------- *
   *
   * Each region of the system has things that live only there, so travelling
   * somewhere new is a different fight and not a recolour. The Forge Belt
   * armours up, the Scrapfields repair each other, the Deep Well hides.
   * ----------------------------------------------------------------- */

  {
    // Forge Belt. Fast and brittle, arrives in showers.
    id: 'cinder',
    name: 'Cinder',
    behaviour: 'fan',
    hp: 10,
    speed: 88,
    size: 11,
    value: 1,
    damage: 3,
    stageMin: 2.4,
    stageMax: 3.0,
    colour: 0xff8a3d,
  },
  {
    // Forge Belt. Slag-plated: flat armour, so chip damage bounces off it.
    id: 'slagback',
    name: 'Slagback',
    behaviour: 'armoured',
    hp: 130,
    speed: 30,
    size: 25,
    value: 3,
    damage: 14,
    stageMin: 4.2,
    stageMax: 5.0,
    colour: 0xff7a3d,
    armour: 9,
  },
  {
    // Outer Rim. Enormous, slow, and it does not care about your slows.
    id: 'rime-drifter',
    name: 'Rime Drifter',
    behaviour: 'armoured',
    hp: 260,
    speed: 20,
    size: 32,
    value: 4,
    damage: 18,
    stageMin: 5.0,
    stageMax: 5.8,
    colour: 0x8ad0ff,
    armour: 6,
  },
  {
    // Scrapfields. Rebuilds everything on nearby bearings. Kill it first.
    id: 'scrapper',
    name: 'Scrapper',
    behaviour: 'healer',
    hp: 110,
    speed: 34,
    size: 22,
    value: 3,
    damage: 8,
    stageMin: 4.0,
    stageMax: 4.8,
    colour: 0x5fe3c0,
    healRate: 16,
    healArc: 0.8,
  },
  {
    // Deep Well. A wraith that also lies about its bearing.
    id: 'void-moth',
    name: 'Void Moth',
    behaviour: 'cloak',
    hp: 90,
    speed: 62,
    size: 19,
    value: 3,
    damage: 11,
    stageMin: 3.2,
    stageMax: 4.0,
    colour: 0x9d6bff,
    cloakRadius: 120,
  },
  {
    // Beyond. Whatever's left of something that used to be a ship.
    id: 'husk',
    name: 'Husk',
    behaviour: 'armoured',
    hp: 190,
    speed: 44,
    size: 24,
    value: 3,
    damage: 16,
    stageMin: 3.6,
    stageMax: 4.4,
    colour: 0xb0bfd0,
    armour: 12,
  },
];
